import React, { useState } from 'react';
import {
  Box,
  Container,
  SimpleGrid,
  Text,
  Image,
  VStack,
  HStack,
  Heading,
  Button,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Product, ProductCategory } from '../../types/product';
import { useProducts } from '../../contexts/ProductContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveProductImageUrl } from '../../services/api';

const PLACEHOLDER_IMAGE = '/images/product-placeholder.jpg';

// Zimbabwe Cities and Towns
const ZIMBABWE_CITIES = [
  // Harare
  'Harare',
  'Chitungwiza',
  
  // Bulawayo
  'Bulawayo',
  
  // Manicaland
  'Mutare',
  'Chipinge',
  'Birchenough Bridge',
  'Nyanga',
  
  // Mashonaland Central
  'Bindura',
  'Chinhoyi',
  'Centenary',
  'Kariba',
  'Karoi',
  'Mount Darwin',
  
  // Mashonaland East
  'Marondera',
  'Murambinda',
  'Rusape',
  'Nyazura',
  'Macheke',
  'Chivhu',
  'Tengwe',
  
  // Mashonaland West
  'Kadoma',
  'Chegutu',
  'Kwekwe',
  'Norton',
  'Murehwa',
  'Shamva',
  
  // Matabeleland North
  'Hwange',
  'Victoria Falls',
  'Binga',
  'Lupane',
  'Nkayi',
  
  // Matabeleland South
  'Gwanda',
  'Kezi',
  'Figtree',
  'Insiza',
  'Umzingwane',
  
  // Midlands
  'Gweru',
  'Shurugwi',
  'Redcliff',
  'Zvishavane',
  'Gokwe',
  'Chirumanzu',
  
  // Masvingo
  'Masvingo',
  'Chivi',
  'Chiredzi',
  'Mwenezi',
  'Bikita',
  'Gutu',
  'Zaka',
];

// Product Categories
const productCategories: { label: string; value: ProductCategory }[] = [
  { label: 'Grains & Cereals', value: 'grains' },
  { label: 'Vegetables', value: 'vegetables' },
  { label: 'Fruits', value: 'fruits' },
  { label: 'Livestock', value: 'livestock' },
  { label: 'Dairy', value: 'dairy' },
  { label: 'Seeds & Seedlings', value: 'seeds' },
  { label: 'Fertilizers & Soil Care', value: 'fertilizers' },
  { label: 'Pesticides & Chemicals', value: 'pesticides' },
  { label: 'Generators & Power', value: 'generators' },
  { label: 'Water Pumps & Irrigation', value: 'pumps' },
  { label: 'Irrigation Systems', value: 'irrigation' },
  { label: 'Heavy Machinery & Tractors', value: 'machinery' },
  { label: 'Farm Tools & Implements', value: 'farm_tools' },
  { label: 'Animal Feeds', value: 'animal_feeds' },
  { label: 'Veterinary Supplies', value: 'veterinary' },
  { label: 'Other', value: 'other' },
];

// ─── ProductCard Component ────────────────────────────────────────────
interface ProductCardProps {
  product: Product;
  onImageError: (productId: string | undefined) => void;
  failedImages: Set<string>;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onImageError, failedImages }) => {
  const categoryColors: Record<string, string> = {
    grains: 'primary',
    vegetables: 'green',
    fruits: 'orange',
    livestock: 'brown',
    dairy: 'blue',
    seeds: 'teal',
    fertilizers: 'green',
    pesticides: 'red',
    generators: 'purple',
    pumps: 'cyan',
    irrigation: 'blue',
    machinery: 'purple',
    farm_tools: 'orange',
    animal_feeds: 'yellow',
    veterinary: 'pink',
    other: 'gray',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.3 }}
    >
      <Box
        as={RouterLink}
        to={`/marketplace/${product._id}`}
        _hover={{ textDecoration: 'none' }}
        h="full"
      >
        <Box
          h="full"
          bg="white"
          border="1px solid"
          borderColor="neutral.200"
          borderRadius="8px"
          overflow="hidden"
          boxShadow="0 1px 3px rgba(0, 0, 0, 0.05)"
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          _hover={{
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
            borderColor: 'primary.200',
          }}
          display="flex"
          flexDirection="column"
        >
          {/* Image Container */}
          <Box position="relative" overflow="hidden" bg="neutral.100" h="200px">
            <Image
              src={failedImages.has(product._id || '') ? PLACEHOLDER_IMAGE : resolveProductImageUrl(product.imageUrl)}
              alt={product.name}
              h="full"
              w="full"
              objectFit="cover"
              onError={() => onImageError(product._id)}
              loading="lazy"
              fallbackSrc={PLACEHOLDER_IMAGE}
              transition="transform 0.3s ease"
              _groupHover={{ transform: 'scale(1.05)' }}
            />
            
            {/* Category & Seller Badges */}
            <HStack position="absolute" top={3} left={3} spacing={2}>
              <Badge
                colorScheme={categoryColors[product.category] || 'gray'}
                fontSize="xs"
                px={3}
                py={1}
                borderRadius="full"
                fontWeight="600"
                bg={`${categoryColors[product.category] || 'gray'}.500`}
                color="white"
                textTransform="capitalize"
              >
                {product.category.replace('_', ' ')}
              </Badge>
              {product.sellerType === 'agro_dealer' && (
                <Badge
                  colorScheme="purple"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="full"
                  fontWeight="700"
                  bg="purple.600"
                  color="white"
                >
                  Agro Dealer
                </Badge>
              )}
            </HStack>
          </Box>

          {/* Content Section */}
          <VStack p={4} align="stretch" spacing={2} flex={1}>
            {/* Product Title */}
            <Heading
              size="sm"
              color="neutral.900"
              noOfLines={2}
              fontSize="15px"
              fontWeight="600"
              lineHeight="1.4"
            >
              {product.name}
            </Heading>

            {/* Product Description */}
            <Text
              fontSize="13px"
              color="neutral.600"
              noOfLines={2}
              lineHeight="1.4"
            >
              {product.description}
            </Text>

            {/* Location */}
            <Text
              fontSize="12px"
              color="neutral.500"
              noOfLines={1}
              fontWeight="500"
            >
              📍 {product.farmLocation}
            </Text>

            {/* Price Section */}
            <HStack justify="space-between" pt={2} borderTop="1px solid" borderColor="neutral.100">
              <VStack align="start" spacing={0}>
                <Text fontSize="11px" color="neutral.500" fontWeight="500">
                  Price
                </Text>
                <Text
                  fontSize="16px"
                  fontWeight="700"
                  color="primary.500"
                >
                  ${product.price}
                </Text>
              </VStack>
              <VStack align="end" spacing={0}>
                <Text fontSize="11px" color="neutral.500" fontWeight="500">
                  Unit
                </Text>
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color="neutral.700"
                >
                  {product.unit}
                </Text>
              </VStack>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </motion.div>
  );
};

const MarketplaceListing: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const { user } = useAuth();
  const { products } = useProducts();
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = (productId: string | undefined) => {
    if (productId) {
      setFailedImages(prev => new Set(prev).add(productId));
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesLocation = !selectedLocation || product.city === selectedLocation;
    return matchesSearch && matchesCategory && matchesLocation;
  });

  return (
    <Box bg="white" minH="100vh" py={12}>
      <Container maxW="container.xl">
        <VStack spacing={12} align="stretch">
          {/* Header Section */}
          <Box
            bg="linear-gradient(135deg, rgba(26, 94, 63, 0.05) 0%, rgba(26, 94, 63, 0.02) 100%)"
            borderRadius="12px"
            border="1px solid"
            borderColor="primary.100"
            py={{ base: 8, md: 10 }}
            px={{ base: 6, md: 8 }}
            textAlign="center"
          >
            <VStack spacing={3} align="center">
              <Text
                fontSize="13px"
                fontWeight="600"
                color="primary.500"
                textTransform="uppercase"
                letterSpacing="1px"
              >
                Marketplace
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: '32px', md: '44px' }}
                fontWeight="900"
                color="neutral.900"
                lineHeight="1.2"
                letterSpacing="-0.5px"
              >
                Agricultural Products
              </Heading>
              <Text
                fontSize={{ base: '14px', md: '16px' }}
                color="neutral.600"
                maxW="650px"
                lineHeight="1.7"
                fontWeight="500"
              >
                Browse farm products directly from verified suppliers across Zimbabwe. Find the best prices and connect with the suppliers.
              </Text>
            </VStack>
          </Box>

          {/* Search & Filter Section */}
          <VStack spacing={4} align="stretch">
            <HStack spacing={3} wrap={{ base: 'wrap', md: 'nowrap' }}>
              {/* Search Input */}
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="neutral.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search products by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  borderColor="neutral.200"
                  _focus={{
                    borderColor: 'primary.500',
                    boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                  }}
                  fontSize="14px"
                  py={6}
                />
              </InputGroup>

              {/* Location Filter */}
              <Select
                size="lg"
                placeholder="All Locations"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                minW={{ base: 'full', md: '180px' }}
                borderColor="neutral.200"
                _focus={{
                  borderColor: 'primary.500',
                  boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                }}
                color="neutral.700"
                fontSize="14px"
                py={6}
              >
                {ZIMBABWE_CITIES.map(city => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </Select>

              {/* Category Filter */}
              <Select
                size="lg"
                placeholder="All Categories"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                minW={{ base: 'full', md: '200px' }}
                borderColor="neutral.200"
                _focus={{
                  borderColor: 'primary.500',
                  boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                }}
                color="neutral.700"
                fontSize="14px"
                py={6}
              >
                {productCategories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </Select>
            </HStack>

            {/* Results Count */}
            <HStack justify="space-between">
              <Text fontSize="13px" color="neutral.500">
                Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
              </Text>
              {(selectedLocation || selectedCategory) && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => {
                    setSelectedLocation('');
                    setSelectedCategory('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </HStack>
          </VStack>

          {/* Verification Check for Buyers */}
          {user?.role === 'buyer' && !user?.isVerified ? (
            <Box
              textAlign="center"
              py={16}
              px={4}
              bg="yellow.50"
              borderRadius="8px"
              border="1px solid"
              borderColor="yellow.200"
            >
              <Text fontSize="18px" fontWeight="600" color="yellow.800" mb={2}>
                Verification Pending
              </Text>
              <Text fontSize="15px" color="yellow.700">
                Your account is pending verification. Please wait for an admin to review your business details before you can view products on the marketplace.
              </Text>
            </Box>
          ) : (
            <>
              {/* Products Grid */}
              {filteredProducts.length > 0 ? (
                <SimpleGrid
                  columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
                  spacing={6}
                  w="full"
                >
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      onImageError={handleImageError}
                      failedImages={failedImages}
                    />
                  ))}
                </SimpleGrid>
              ) : (
                /* Empty State */
                <Box
                  textAlign="center"
                  py={16}
                  px={4}
                  bg="neutral.50"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="neutral.200"
                >
                  <Text fontSize="16px" fontWeight="600" color="neutral.900" mb={2}>
                    No products found
                  </Text>
                  <Text fontSize="14px" color="neutral.600">
                    Try adjusting your search, location, or category filters
                  </Text>
                </Box>
              )}
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default MarketplaceListing;
